/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 07/12/21
 */

import { AuthState, isLoginError } from '@/components/auth/AuthProvider';
import usePageTitle from '@/utils/hooks/usePageTitle';
import { Box, Button, Center, Container, Heading, HStack, Link, Text, VStack } from '@chakra-ui/react';
import React, { PropsWithChildren } from 'react';
import { useAuth } from '../auth/useAuth';
import config from '@/config';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface LoginProps {}

const Login: React.FC<PropsWithChildren<LoginProps>> = () => {
    usePageTitle('Log in');
    const { login, authState } = useAuth();

    const handleLogin = async () => {
        await login();
    };

    const renderError = (error: AuthState['error']) => {
        if (error) {
            let message = 'Could not login.';
            if (isLoginError(error)) {
                message = error.message;
            } else if (typeof error === 'string') {
                message = error;
            }

            if (!message.endsWith('.')) message += '.';

            const support =
                message !== 'Token expired.' ? (
                    <>
                        Please take a screenshot of this error and inform the People Unit via{' '}
                        <Link
                            href={
                                'https://uts.service-now.com/serviceconnect?id=sc_cat_item&sys_id=58218bf3db82c010af95401d34961915&sysparm_category=1b598934db2cac10dca1147a3a961963'
                            }
                            target="_blank"
                        >
                            ServiceConnect
                        </Link>
                    </>
                ) : (
                    <>Please login again.</>
                );

            return (
                <Box aria-live="polite">
                    <Text as="b">Login error:</Text>
                    <br />
                    <Text>
                        {message} {support}
                    </Text>
                </Box>
            );
        }
    };

    return (
        <Container mt={20} h={'calc(100vh - 200px)'}>
            <VStack alignItems="stretch">
                <Center>
                    <VStack spacing={8}>
                        <Heading size={'md'}>Welcome to the UTS Work Planning Tool (WPT)</Heading>
                        <Text>Please select an option below:</Text>
                        <Button border="2px" onClick={handleLogin}>
                            Log in
                        </Button>

                        <HStack>
                            {config.urls.aiAssistant && (
                                <>
                                    <Link href={config.urls.aiAssistant} isExternal>
                                        WPT AI Assistant
                                    </Link>
                                    &nbsp;&nbsp;|
                                </>
                            )}
                            <Link href={'https://studentutsedu.sharepoint.com/sites/UTSWorkPlanningTool'} isExternal>
                                WPT Resources
                            </Link>
                        </HStack>
                        {renderError(authState.error)}

                        {/*{Date.now() < new Date('2025-05-19T04:00:00.000+10:00').getTime() && (*/}
                        {/*    <Alert status="info">*/}
                        {/*        <AlertIcon />*/}
                        {/*        <AlertDescription>*/}
                        {/*            Work Planning Tool will be unavailable Sunday, 18th May 2025, 10:00PM until Monday,*/}
                        {/*            19th May 2025 01:00 AM due to a planned outage.*/}
                        {/*        </AlertDescription>*/}
                        {/*    </Alert>*/}
                        {/*)}*/}
                    </VStack>
                </Center>
            </VStack>
        </Container>
    );
};

export default Login;
